import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { getLastIntrospectNodeByOperationAndParam } from '@adornis/introspect/api/get-introspect-node.js';
import { IntrospectViewer } from '@adornis/introspect/client/x-introspect-viewer.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { of } from 'rxjs';
import { setContactControlled } from '../../api/clienthelper.js';
import { addCommentToContactEntity } from '../../api/comments.js';
import { Comment } from '../../entities/comment.js';
import { ContactEntity } from '../../entities/contact.js';

export const ContactDetailView = stateful((props: { contactID: string; onClose: () => void }) => {
  const [newNote, setNewNote] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showContactIntrospection, setShowContactIntrospection] = useState<boolean>(false);

  const contact = useQuery(
    () =>
      getByID(
        ContactEntity,
        props.contactID,
      )({
        _id: 1,
        _class: 1,
        timestamp: 1,
        companyName: 1,
        companyAction: 1,
        companyUpdates: 1,
        personAction: 1,
        personUpdates: 1,
        email: 1,
        phone: 1,
        position: 1,
        location: 1,
        status: 1,
        notes: selectionSet(() => Comment, 1),
        isCheckedBySupport: 1,
      }),
    [props.contactID, refreshTrigger],
  );

  const introspectionContactNode = useQuery(() => {
    if (!props.contactID) return of(null);
    return getLastIntrospectNodeByOperationAndParam(
      'introspectContact',
      JSON.stringify({ contactID: props.contactID }),
    )({ _id: 1 });
  }, [props.contactID]);

  const handleSetContactControlled = async () => {
    try {
      await setContactControlled(props.contactID)();
      // Refresh the contact data after setting controlled state
      setRefreshTrigger(refreshTrigger + 1);
    } catch (error) {
      logger.error({ err: error }, 'Failed to set contact controlled state');
    }
  };

  // Handle note submission
  const handleSubmitNote = async () => {
    if (!newNote.trim()) return;

    try {
      setIsSubmitting(true);
      setToastMessage(null);

      await addCommentToContactEntity(newNote.trim(), props.contactID)({});
      setNewNote('');

      // Refresh the data
      setRefreshTrigger(refreshTrigger + 1);

      // Show success toast
      setToastMessage({ type: 'success', message: 'Note added successfully!' });

      // Auto-dismiss toast after 3 seconds
      setTimeout(() => {
        setToastMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Failed to add note:', error);
      setToastMessage({ type: 'error', message: 'Failed to add note' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render notes
  const renderNotes = (notes: Comment[]) => {
    if (!notes || notes.length === 0) {
      return html`<div class="text-sm italic text-base-content/50 p-4 text-center">No notes yet</div>`;
    }

    return html`
      <div class="space-y-3">
        ${notes.map(
          (note, index) => html`
            <div class="bg-base-200 p-3 rounded-lg">
              <div class="flex justify-between items-start mb-2">
                <div class="text-xs text-base-content/40">
                  ${note.createdAt ? format(note.createdAt, 'PPp') : 'Unknown date'}
                </div>
                <div class="text-xs text-base-content/50">#${index + 1}</div>
              </div>
              <div class="text-sm whitespace-pre-wrap">${note.text}</div>
            </div>
          `,
        )}
      </div>
    `;
  };

  // Show loading state if contact data is not yet loaded
  if (!contact.value) {
    return html`
      <div class="card bg-base-100 shadow-xl border border-base-300 h-full">
        <div class="card-body flex items-center justify-center">
          <span class="loading loading-spinner loading-lg text-secondary"></span>
        </div>
      </div>
    `;
  }

  return html`
    <div class="card bg-base-100 shadow-xl border border-base-300 h-full">
      <div class="card-body">
        <div class="flex justify-between items-center">
          <h2 class="card-title text-xl font-bold">Contact Details</h2>
          <button class="btn btn-sm btn-circle btn-ghost" @click=${props.onClose} title="Close">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-5 h-5"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <p class="text-sm text-info">ID: ${contact.value._id}</p>
        <div class="divider"></div>

        <div class="space-y-4 overflow-y-auto max-h-[calc(100vh-300px)]">
          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Company Name</span>
              <div class="flex space-x-2 ml-auto">
                ${introspectionContactNode.value
                  ? html`
                      <button
                        class="btn btn-xs btn-outline"
                        @click=${() => setShowContactIntrospection(!showContactIntrospection)}
                      >
                        ${showContactIntrospection ? 'Hide Introspection Contact' : 'Show Introspection Contact'}
                      </button>
                    `
                  : nothing}
              </div>
            </label>
            <div class="bg-base-200 p-3 rounded-lg text-sm">${contact.value.companyName || 'N/A'}</div>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Status</span>
            </label>
            <div class="bg-base-200 p-3 rounded-lg text-sm">
              <span
                class="badge ${contact.value.status === 'active' || contact.value.status === 'customer'
                  ? 'badge-success'
                  : contact.value.status === 'lead' || contact.value.status === 'prospect'
                    ? 'badge-warning'
                    : 'badge-neutral'}"
                >${contact.value.status || 'N/A'}</span
              >
            </div>
          </div>

          <! -- Handle set support checked-->
          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Already Checked</span>
              <span class="label-text-alt">Is checked by support</span>
            </label>
            <div class="bg-base-200 rounded-lg">
              <input
                type="checkbox"
                class="bg-base-200 text-sm resize-none"
                .checked=${contact.value.isCheckedBySupport}
                @click=${(e: Event) => handleSetContactControlled()}
              />
            </div>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Email</span>
            </label>
            <div class="bg-base-200 p-3 rounded-lg text-sm">
              ${contact.value.email
                ? html`<a href="mailto:${contact.value.email}" class="link link-primary">${contact.value.email}</a>`
                : 'N/A'}
            </div>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Phone</span>
            </label>
            <div class="bg-base-200 p-3 rounded-lg text-sm">
              ${contact.value.phone
                ? html`<a href="tel:${contact.value.phone}" class="link link-primary">${contact.value.phone}</a>`
                : 'N/A'}
            </div>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Position</span>
            </label>
            <div class="bg-base-200 p-3 rounded-lg text-sm">${contact.value.position || 'N/A'}</div>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Location</span>
            </label>
            <div class="bg-base-200 p-3 rounded-lg text-sm">${contact.value.location || 'N/A'}</div>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Company Action</span>
            </label>
            <div class="bg-base-200 p-3 rounded-lg text-sm whitespace-pre-wrap">
              ${contact.value.companyAction || 'N/A'}
            </div>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Company Updates</span>
            </label>
            <div class="bg-base-200 p-3 rounded-lg text-sm whitespace-pre-wrap">
              ${contact.value.companyUpdates || 'N/A'}
            </div>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Person Action</span>
            </label>
            <div class="bg-base-200 p-3 rounded-lg text-sm whitespace-pre-wrap">
              ${contact.value.personAction || 'N/A'}
            </div>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Person Updates</span>
            </label>
            <div class="bg-base-200 p-3 rounded-lg text-sm whitespace-pre-wrap">
              ${contact.value.personUpdates || 'N/A'}
            </div>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Timestamp</span>
            </label>
            <div class="bg-base-200 p-3 rounded-lg text-sm">
              ${contact.value.timestamp ? format(contact.value.timestamp, 'PPpp') : 'N/A'}
            </div>
          </div>

          <!-- Notes Section -->
          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Notes</span>
              <span class="label-text-alt">${contact.value.notes?.length || 0} notes</span>
            </label>
            <div class="bg-base-200 rounded-lg">${renderNotes(contact.value.notes)}</div>
          </div>

          <!-- Add Note Form -->
          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Add Note</span>
            </label>
            <div class="relative">
              <textarea
                class="textarea textarea-bordered w-full h-24 bg-base-200 text-sm resize-none"
                placeholder="Add a note..."
                .value=${newNote}
                @input=${(e: InputEvent) => setNewNote((e.target as HTMLTextAreaElement).value)}
              ></textarea>
              <div class="absolute bottom-3 right-3">
                <button
                  class="btn btn-sm btn-primary"
                  @click=${handleSubmitNote}
                  ?disabled=${!newNote.trim() || isSubmitting}
                >
                  ${isSubmitting
                    ? html`<span class="loading loading-spinner loading-xs mr-1"></span> Saving...`
                    : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>

        ${showContactIntrospection
          ? html`
              <div class="modal modal-open">
                <div class="modal-box w-full h-full p-0 relative max-w-full rounded-none">
                  ${IntrospectViewer({ nodeID: introspectionContactNode.value?._id })}
                  <button
                    class="btn btn-sm btn-circle absolute right-2 top-2"
                    @click=${() => setShowContactIntrospection(false)}
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>
            `
          : nothing}

        <!-- Toast Message -->
        ${toastMessage
          ? html`
              <div class="toast toast-end ${toastMessage.type === 'success' ? 'toast-success' : 'toast-error'}">
                <div class="alert">
                  <span>${toastMessage.message}</span>
                </div>
              </div>
            `
          : ''}
      </div>
    </div>
  `;
});
